import { createContext, use } from 'react';
import { CHAVES_PREFERENCIA, gravarPreferencia, lerPreferencia } from './preferencias';

export type Tema = 'claro' | 'escuro';

export interface EstadoTema {
  tema: Tema;
  definirTema: (tema: Tema) => void;
  alternarTema: () => void;
}

export const ContextoTema = createContext<EstadoTema | null>(null);

export function useTema(): EstadoTema {
  const contexto = use(ContextoTema);
  if (!contexto) throw new Error('useTema precisa estar dentro de <Providers>.');
  return contexto;
}

const ehTema = (valor: string | null): valor is Tema => valor === 'claro' || valor === 'escuro';

/**
 * Tema inicial: a escolha explícita da pessoa, se houver; senão, a preferência do sistema.
 *
 * A escolha é lembrada no navegador (e não no servidor) porque é preferência do **aparelho**: a
 * mesma pessoa pode querer escuro no monitor da noite e claro no notebook de dia. É dado de enfeite,
 * sem nada sensível — exatamente o que `lib/preferencias.ts` autoriza a guardar ali.
 *
 * A marca é dourado sobre grafite: o escuro é o tema de casa. Mas quem pede claro pelo sistema
 * recebe claro, porque acessibilidade vence estética.
 */
export function temaInicial(): Tema {
  const guardado = lerPreferencia(CHAVES_PREFERENCIA.tema);
  if (ehTema(guardado)) return guardado;
  if (typeof window === 'undefined' || !window.matchMedia) return 'escuro';
  return window.matchMedia('(prefers-color-scheme: light)').matches ? 'claro' : 'escuro';
}

export function guardarTema(tema: Tema): void {
  gravarPreferencia(CHAVES_PREFERENCIA.tema, tema);
}
